import { useRef, useState, type ReactNode } from 'react';
import { ApiError, api } from '../../api/client';
import type { Atelier, Demande, FakKit, Personne, Pole, SeanceProgramme } from '../../types';
import { formatDateLong, formatHoraire, todayISO, typeUrgenceAuto } from '../annuaire/format';
import { PoleDot } from '../demande/PoleDot';
import { useFenetreModale } from '../demande/useFenetreModale';

// Les fenêtres de la console Urgence : déclarer (depuis une séance, ou « autre cas »), et les trois issues. Chacune est une vraie fenêtre
// (le focus y entre, Tab y reste, Échap la ferme, le focus revient où il était) — elles étaient de simples blocs sans clavier.

export type TypeUrgence = 'urgence_weekend' | 'urgence_semaine';

const JOURS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
/** « Intrigues artistiques I · jeudi 10:00 · Beaulieu » : deux ateliers du même nom se distinguent. */
export function libelleAtelier(a: Atelier): string {
  const quand = [a.jour_semaine ? JOURS[a.jour_semaine] : '', a.heure_debut ? a.heure_debut.slice(0, 5) : ''].filter(Boolean).join(' ');
  return [a.nom, quand, a.lieu ?? ''].filter(Boolean).join(' · ');
}

export const AIDE_NOTES = 'Aucune information médicale ou personnelle : ces notes peuvent être lues par la personne absente et par son/sa remplaçant·e.';

/** Une demande existe déjà pour la séance (réponse 409 du serveur) : laquelle, pour l'ouvrir. */
export interface Doublon { message: string; demandeId: number }
export function doublonDepuis(err: unknown): Doublon | null {
  if (err instanceof ApiError && err.status === 409 && typeof err.corps.demande_id === 'number') return { message: err.message, demandeId: err.corps.demande_id };
  return null;
}
export function MessageDoublon({ doublon }: { doublon: Doublon }) {
  return (
    // Pas le badge d'erreur du kit : il met tout en capitales, illisible sur une phrase entière.
    <div className="mb-12 text-sm u-erreur" role="alert">
      <span>{doublon.message}</span>
      <a className="btn btn-sm" href={`#demandes/${doublon.demandeId}`} target="_blank" rel="noreferrer">Ouvrir la demande (nouvel onglet)</a>
    </div>
  );
}

function Fenetre({ titre, onClose, pied, children, focusInitial, bloquee }: {
  titre: string; onClose: () => void; pied: ReactNode; children: ReactNode; focusInitial?: () => HTMLElement | null; bloquee?: boolean;
}) {
  const { ref } = useFenetreModale(() => { if (!bloquee) onClose(); }, { focusInitial });
  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="u-modal-t" ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3 className="modal-title" id="u-modal-t">{titre}</h3><button type="button" className="close-btn" aria-label="Fermer" onClick={onClose}>✕</button></div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">{pied}</div>
      </div>
    </div>
  );
}

function Erreur({ message }: { message: string | null }) {
  return message ? <div className="u-erreur mb-12" role="alert">{message}</div> : null;
}

/** Le/la titulaire, et qui est programmé·e ce jour-là : une phrase qui dit lequel des deux on a pré-rempli. */
function phraseAbsent(titulaire: string | null, anime: string | null): string {
  if (anime && titulaire && anime !== titulaire) return `Titulaire : ${titulaire} — mais c'est ${anime} qui est programmé·e ce jour-là : vérifiez avec l'appel.`;
  if (anime) return `C'est ${anime} qui anime cette séance.`;
  if (titulaire) return `Titulaire : ${titulaire}.`;
  return "Aucun·e titulaire ni animateur·rice connu·e pour cette séance : précisez qui est absent·e.";
}

/** Déclarer une absence urgente depuis une séance. Le contexte (atelier, date) vient du clic ; la personne absente est celle qui ANIME ce jour-là (sinon le/la titulaire). */
export function ModalDeclarer({ atelier, seance, personnes, pole, onClose, onDone }: {
  atelier: Atelier; seance: SeanceProgramme; personnes: Personne[]; pole: Pole | null; onClose: () => void; onDone: (d: Demande) => void;
}) {
  const nom = (id: number | null | undefined) => personnes.find((p) => p.id === id)?.display_name ?? null;
  const [absentId, setAbsentId] = useState<number | ''>(seance.animateur_personne_id ?? atelier.titulaire_personne_id ?? '');
  const [typeOverride, setTypeOverride] = useState<TypeUrgence | null>(null);
  const [notes, setNotes] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doublon, setDoublon] = useState<Doublon | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const type: TypeUrgence = typeOverride ?? typeUrgenceAuto(seance.date);

  const valider = async () => {
    if (!absentId) { setError('La personne absente est requise.'); select.current?.focus(); return; }
    setEnvoi(true); setError(null); setDoublon(null);
    try {
      onDone(await api.createUrgence({ absent_personne_id: Number(absentId), atelier_id: atelier.id, date_atelier: seance.date, type, notes: notes.trim() || undefined }));
    } catch (err) {
      const d = doublonDepuis(err);
      if (d) setDoublon(d); else setError(err instanceof Error ? err.message : 'Échec de la création');
    } finally { setEnvoi(false); }
  };

  return (
    <Fenetre titre="Déclarer une absence urgente" onClose={onClose} bloquee={envoi} focusInitial={() => select.current}
      pied={<><button type="button" className="btn" onClick={onClose}>Retour</button><button type="button" className="btn btn-danger" onClick={valider} disabled={envoi}>{envoi ? 'Création…' : 'Déclencher la cascade'}</button></>}>
      <Erreur message={error} />
      {doublon && <MessageDoublon doublon={doublon} />}
      <div className="u-modal-seance">
        <div className="font-medium"><PoleDot couleur={pole?.couleur} /> {atelier.nom}</div>
        <div className="text-sm text-muted mt-4">{formatDateLong(seance.date)} · {formatHoraire(seance.heure_debut, seance.heure_fin)}{seance.lieu ? ` · ${seance.lieu}` : ''}{pole?.nom ? ` · ${pole.nom}` : ''}</div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="u-abs">Qui est absent·e ?</label>
        <select className="form-select" id="u-abs" ref={select} value={absentId} onChange={(e) => setAbsentId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">—</option>
          {personnes.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
        </select>
        <p className="u-hint">{phraseAbsent(nom(atelier.titulaire_personne_id), nom(seance.animateur_personne_id))}</p>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="u-notes">Notes de l'appel <span className="text-muted">(facultatif)</span></label>
        <textarea className="form-textarea" id="u-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <p className="u-hint">{AIDE_NOTES}</p>
      </div>
      <p className="u-hint">
        {type === 'urgence_weekend' ? 'Urgence week-end' : 'Urgence semaine'}, d'après le jour de la séance.{' '}
        <button type="button" className="u-lien" onClick={() => setTypeOverride(type === 'urgence_weekend' ? 'urgence_semaine' : 'urgence_weekend')}>
          Choisir {type === 'urgence_weekend' ? 'semaine' : 'week-end'}
        </button>
      </p>
    </Fenetre>
  );
}

/** Une séance qui n'est pas dans la liste (date plus lointaine, atelier sans séance au programme). */
export function ModalAutreCas({ ateliers, personnes, onClose, onDone }: {
  ateliers: Atelier[]; personnes: Personne[]; onClose: () => void; onDone: (d: Demande) => void;
}) {
  const [absentId, setAbsentId] = useState<number | ''>('');
  const [atelierId, setAtelierId] = useState<number | ''>('');
  const [date, setDate] = useState(() => todayISO());
  const [typeOverride, setTypeOverride] = useState<TypeUrgence | null>(null);
  const [notes, setNotes] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doublon, setDoublon] = useState<Doublon | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const type: TypeUrgence = typeOverride ?? typeUrgenceAuto(date || todayISO());

  const valider = async () => {
    if (!absentId || !atelierId || !date) { setError('Personne absente, atelier et date sont requis.'); return; }
    setEnvoi(true); setError(null); setDoublon(null);
    try {
      onDone(await api.createUrgence({ absent_personne_id: Number(absentId), atelier_id: Number(atelierId), date_atelier: date, type, notes: notes.trim() || undefined }));
    } catch (err) {
      const d = doublonDepuis(err);
      if (d) setDoublon(d); else setError(err instanceof Error ? err.message : 'Échec de la création');
    } finally { setEnvoi(false); }
  };

  return (
    <Fenetre titre="Déclarer un autre cas" onClose={onClose} bloquee={envoi} focusInitial={() => select.current}
      pied={<><button type="button" className="btn" onClick={onClose}>Retour</button><button type="button" className="btn btn-danger" onClick={valider} disabled={envoi}>{envoi ? 'Création…' : 'Déclencher la cascade'}</button></>}>
      <p className="u-hint" style={{ marginTop: 0 }}>Pour une séance absente de la liste : date plus lointaine, ou atelier sans séance au programme.</p>
      <Erreur message={error} />
      {doublon && <MessageDoublon doublon={doublon} />}
      <div className="form-group">
        <label className="form-label" htmlFor="u-a1">Personne absente *</label>
        <select className="form-select" id="u-a1" ref={select} value={absentId} onChange={(e) => setAbsentId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">—</option>
          {personnes.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="u-a2">Atelier *</label>
        <select className="form-select" id="u-a2" value={atelierId} onChange={(e) => setAtelierId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">—</option>
          {ateliers.map((a) => <option key={a.id} value={a.id}>{libelleAtelier(a)}</option>)}
        </select>
        <p className="u-hint">Les ateliers se reconnaissent à leur jour, leur heure et leur lieu.</p>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="u-a3">Date *</label>
        <input type="date" className="form-input" id="u-a3" style={{ maxWidth: 200 }} value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="u-a4">Notes de l'appel <span className="text-muted">(facultatif)</span></label>
        <textarea className="form-textarea" id="u-a4" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <p className="u-hint">{AIDE_NOTES}</p>
      </div>
      <p className="u-hint">
        {type === 'urgence_weekend' ? 'Urgence week-end' : 'Urgence semaine'}, d'après le jour de la séance.{' '}
        <button type="button" className="u-lien" onClick={() => setTypeOverride(type === 'urgence_weekend' ? 'urgence_semaine' : 'urgence_weekend')}>
          Choisir {type === 'urgence_weekend' ? 'semaine' : 'week-end'}
        </button>
      </p>
    </Fenetre>
  );
}

export function ModalFusion({ demande, ateliers, onClose, onDone }: { demande: Demande; ateliers: Atelier[]; onClose: () => void; onDone: (d: Demande) => void }) {
  const [cibleId, setCibleId] = useState<number | ''>('');
  const [lieuHoraire, setLieuHoraire] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const valider = async () => {
    if (!cibleId) { setError("L'atelier cible est requis."); select.current?.focus(); return; }
    setEnvoi(true); setError(null);
    try { onDone(await api.appliquerAction(demande.id, 'urgence_fusion', { atelier_cible_id: Number(cibleId), lieu_horaire: lieuHoraire.trim() || undefined })); }
    catch (err) { setError(err instanceof Error ? err.message : 'Échec de la fusion'); }
    finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre="Atelier fusionné" onClose={onClose} bloquee={envoi} focusInitial={() => select.current}
      pied={<><button type="button" className="btn" onClick={onClose}>Retour</button><button type="button" className="btn btn-accent" onClick={valider} disabled={envoi}>{envoi ? 'Enregistrement…' : 'Confirmer la fusion'}</button></>}>
      <Erreur message={error} />
      <div className="form-group">
        <label className="form-label" htmlFor="u-cible">Atelier cible *</label>
        <select className="form-select" id="u-cible" ref={select} value={cibleId} onChange={(e) => setCibleId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">—</option>
          {ateliers.map((a) => <option key={a.id} value={a.id}>{libelleAtelier(a)}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="u-lh">Lieu / horaire</label>
        <input className="form-input" id="u-lh" value={lieuHoraire} onChange={(e) => setLieuHoraire(e.target.value)} placeholder="ex : Salle B, 14h00" />
      </div>
    </Fenetre>
  );
}

/** « Maintenu » : avec un·e remplaçant·e (pré-rempli·e quand quelqu'un vient d'accepter), ou en présence contenante si l'atelier l'autorise. */
export function ModalMaintien({ demande, personnes, maintienPossible, fak, remplacantInitial, onClose, onDone }: {
  demande: Demande; personnes: Personne[]; maintienPossible: boolean; fak: FakKit | null; remplacantInitial?: number | null; onClose: () => void; onDone: (d: Demande) => void;
}) {
  const [remplacantId, setRemplacantId] = useState<number | ''>(remplacantInitial ?? '');
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const sansRemplacantBloque = !remplacantId && !maintienPossible;
  const valider = async () => {
    if (sansRemplacantBloque) { setError('Un·e remplaçant·e est requis·e pour cet atelier (le maintien sans remplaçant·e n’y est pas autorisé).'); return; }
    setEnvoi(true); setError(null);
    try { onDone(await api.appliquerAction(demande.id, 'urgence_maintien', remplacantId ? { remplacant_personne_id: Number(remplacantId) } : undefined)); }
    catch (err) { setError(err instanceof Error ? err.message : 'Échec du maintien'); }
    finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre="Atelier maintenu" onClose={onClose} bloquee={envoi} focusInitial={() => select.current}
      pied={<><button type="button" className="btn" onClick={onClose}>Retour</button><button type="button" className="btn btn-accent" onClick={valider} disabled={envoi || sansRemplacantBloque}>{envoi ? 'Enregistrement…' : 'Confirmer le maintien'}</button></>}>
      <Erreur message={error} />
      <div className="form-group">
        <label className="form-label" htmlFor="u-remp">Remplaçant·e <span className="text-muted">(facultatif)</span></label>
        <select className="form-select" id="u-remp" ref={select} value={remplacantId} onChange={(e) => setRemplacantId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">— Présence contenante, sans remplaçant·e —</option>
          {personnes.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
        </select>
      </div>
      <div className="text-sm mb-8">
        FRA : {fak
          ? <span className={fak.statut === 'publie' ? 'badge badge-ok' : 'badge badge-warn'}>{fak.statut}</span>
          : <span className="badge badge-err">manquante</span>}
        {fak?.lien_document && <> — <a href={fak.lien_document} target="_blank" rel="noreferrer">Voir le document</a></>}
      </div>
      {!maintienPossible && (
        <p className="text-sm text-warn">Le maintien sans remplaçant·e (présence contenante) n'est pas autorisé pour cet atelier : choisissez quelqu'un, ou utilisez « Annuler + SMS ».</p>
      )}
    </Fenetre>
  );
}

export function ModalAnnulation({ demande, onClose, onDone }: { demande: Demande; onClose: () => void; onDone: (d: Demande) => void }) {
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const champ = useRef<HTMLTextAreaElement>(null);
  const valider = async () => {
    setEnvoi(true); setError(null);
    try { onDone(await api.appliquerAction(demande.id, 'urgence_annulation', { motif: motif.trim() || undefined })); }
    catch (err) { setError(err instanceof Error ? err.message : "Échec de l'annulation"); }
    finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre="Annuler + SMS" onClose={onClose} bloquee={envoi} focusInitial={() => champ.current}
      pied={<><button type="button" className="btn" onClick={onClose}>Retour</button><button type="button" className="btn btn-danger" onClick={valider} disabled={envoi}>{envoi ? 'Enregistrement…' : "Confirmer l'annulation"}</button></>}>
      <Erreur message={error} />
      <p className="text-sm text-muted mb-12">L'application prépare un SMS pour les participant·e·s. Vous l'enverrez juste après, depuis cette page.</p>
      <div className="form-group">
        <label className="form-label" htmlFor="u-motif">Motif <span className="text-muted">(facultatif)</span></label>
        <textarea className="form-textarea" id="u-motif" ref={champ} rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
    </Fenetre>
  );
}
